import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Row, Col, Typography, Spin, Empty, Tag, Rate, Input, Select, Space } from 'antd';
import { ShoppingOutlined, SearchOutlined } from '@ant-design/icons';
import { productService, storageUrl } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const CategoryDetail = () => {
  const { id } = useParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    fetchProducts();
  }, [id]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = { category_id: id, per_page: 50 };
      const res = await productService.getProducts(params);
      const data = res.data?.data?.data || res.data?.data || res.data || [];
      const productsList = Array.isArray(data) ? data : [];
      setProducts(productsList);
      if (productsList.length > 0) {
        const cat = productsList[0]?.category;
        if (cat) setCategory(cat);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = products
    .filter((p) => !search || p.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sort === 'price-low') return (a.price || 0) - (b.price || 0);
      if (sort === 'price-high') return (b.price || 0) - (a.price || 0);
      if (sort === 'rating') return (b.average_rating || 0) - (a.average_rating || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  if (loading)
    return (
      <div style={{ textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={3} style={{ color: '#2d5a3d', margin: 0 }}>
            {category?.name || 'Category'}
          </Title>
          <Text type="secondary">{filtered.length} product(s)</Text>
        </div>
        <Space>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search in category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200, borderRadius: 10 }}
            allowClear
          />
          <Select
            value={sort}
            onChange={setSort}
            style={{ width: 140, borderRadius: 10 }}
            options={[
              { value: 'newest', label: 'Newest' },
              { value: 'price-low', label: 'Price: Low to High' },
              { value: 'price-high', label: 'Price: High to Low' },
              { value: 'rating', label: 'Highest Rated' },
            ]}
          />
        </Space>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <Empty description="No products found in this category" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((p) => (
            <Col xs={12} sm={8} md={6} lg={6} key={p.id}>
              <Link to={`/customer/products/${p.id}`} style={{ textDecoration: 'none' }}>
                <Card
                  className="customer-card"
                  hoverable
                  cover={
                    <div
                      style={{
                        height: 160,
                        background: '#f0f5f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderRadius: '12px 12px 0 0',
                      }}
                    >
                      {p.images?.[0]?.image_url || p.image_url ? (
                        <img
                          src={storageUrl(p.images?.[0]?.image_url || p.image_url)}
                          alt={p.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ShoppingOutlined style={{ fontSize: 48, color: '#a8e6cf' }} />
                      )}
                    </div>
                  }
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <Text
                    ellipsis
                    style={{ display: 'block', color: '#2d5a3d', fontWeight: 600, marginBottom: 4 }}
                  >
                    {p.name}
                  </Text>
                  {p.average_rating > 0 && (
                    <Rate disabled value={p.average_rating} style={{ fontSize: 12 }} />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 4,
                    }}
                  >
                    <Text strong style={{ color: '#52b788', fontSize: 16 }}>
                      NPR {(p.sale_price ?? p.price) || 0}
                    </Text>
                    {p.sale_price != null && Number(p.sale_price) < Number(p.price) && (
                      <Text delete style={{ color: '#9ca3af', fontSize: 12 }}>
                        NPR {p.price}
                      </Text>
                    )}
                  </div>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default CategoryDetail;
